import styled from "styled-components";
import { colors } from "../variables";

// Each top-level section alternates its background so the page
// reads as distinct bands. `$alt` flips a section from the light-grey base to
// white — the page starts on lightBg (see services.tsx), so section 1 is the
// light band, section 2 is white, section 3 is light again.
export const StyledSection = styled.section<{ $alt: boolean }>`
  background: ${(props) => (props.$alt ? colors.white : colors.lightBg)};
  display: flex;
  justify-content: center;
  padding: 5rem 0 6rem;
  scroll-margin-top: 2rem;

  .amount {
    flex-shrink: 0;
    color: ${colors.primary};
    font-weight: 700;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    padding: 3.5rem 0 4rem;
  }
`;

export const StyledContainer = styled.div`
  width: 1400px;

  @media (max-width: 1600px) {
    width: 1000px;
  }
  @media (max-width: 1144px) {
    width: 90vw;
  }
  @media (max-width: 768px) {
    width: 95vw;
  }
`;

// Big centered heading that opens each section, with a short red accent bar
// underneath it.
export const StyledSectionTitle = styled.h2`
  text-align: center;
  color: ${colors.dark};
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin: 0 0 1rem;

  &:after {
    content: "";
    display: block;
    width: 4rem;
    height: 3px;
    background: ${colors.primary};
    margin: 1.25rem auto 0;
  }

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

// Lead paragraph(s) directly under the section title.
export const StyledIntro = styled.div`
  margin: 0 auto 1.5rem;
  text-align: center;

  p {
    color: ${colors.grayText};
    line-height: 1.8;
    font-size: 1rem;
    margin: 0 0 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  @media (max-width: 768px) {
    margin-bottom: 1.75rem;

    p {
      font-size: 0.9rem;
    }
  }
`;

// Sub-heading used for "ШАГ 1", "ШАГ 2", "Вариант 1", "Дополнительные услуги",
// rendered as a dark banner with a red left accent.
export const StyledStepTitle = styled.h3`
  background: ${colors.dark};
  color: ${colors.white};
  border-left: 4px solid ${colors.primary};
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 0 0.35rem 0.35rem 0;
  margin: 3rem 0 1.5rem;

  @media (max-width: 768px) {
    padding: 0.85rem 1.1rem;
    font-size: 0.85rem;
    margin: 2rem 0 1.25rem;
  }
`;

// Generic body copy inside a section.
export const StyledText = styled.p`
  color: ${colors.grayText};
  line-height: 1.8;
  font-size: 1rem;
  margin: 0 0 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

// Bulleted list with a custom red marker. Reused across every section.
export const StyledList = styled.ul`
  list-style: none;
  margin: 1rem 0 1.5rem;
  padding: 0;

  li {
    position: relative;
    padding-left: 1.5rem;
    color: ${colors.grayText};
    line-height: 1.8;
    font-size: 1rem;
    margin-bottom: 0.65rem;

    &:last-child {
      margin-bottom: 0;
    }

    &:before {
      content: "";
      position: absolute;
      left: 0;
      top: 0.65rem;
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      background: ${colors.primary};
    }
  }

  @media (max-width: 768px) {
    li {
      font-size: 0.9rem;

      &:before {
        top: 0.55rem;
      }
    }
  }
`;

// Highlighted "ВАЖНО" / disclaimer box — light red wash with a red left border.
export const StyledNote = styled.div`
  background: rgba(218, 0, 38, 0.05);
  border-left: 3px solid ${colors.primary};
  border-radius: 0 0.35rem 0.35rem 0;
  padding: 1rem 1.25rem;
  margin: 1.5rem 0;

  p {
    color: ${colors.grayText};
    line-height: 1.7;
    font-size: 0.95rem;
    margin: 0 0 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: ${colors.dark};
    font-weight: 700;
  }

  @media (max-width: 768px) {
    padding: 0.85rem 1rem;

    p {
      font-size: 0.88rem;
    }
  }
`;

// Two consultation-format options presented as an inline strip of pills.
export const StyledOptions = styled.ul`
  list-style: none;
  margin: 1.25rem 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  li {
    flex: 1 1 18rem;
    background: ${colors.white};
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-top: 3px solid ${colors.primary};
    border-radius: 0.5rem;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
    padding: 1rem 1.25rem;
    color: ${colors.grayText};
    font-size: 0.95rem;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    li {
      font-size: 0.9rem;
    }
  }
`;

// The two package cards (Базовый / Расширенный) shown side by side.
export const StyledPackages = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 1.5rem 0;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// One package card. `$featured` (the Расширенный / extended package) gets the
// red accent so the pricier, fuller option visually stands out from the base
// package, which uses the neutral dark accent.
export const StyledPackageCard = styled.div<{ $featured: boolean }>`
  display: flex;
  flex-direction: column;
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-top: 4px solid
    ${(props) => (props.$featured ? colors.primary : colors.dark)};
  border-radius: 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  padding: 1.75rem;

  .package-name {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: ${(props) => (props.$featured ? colors.primary : colors.dark)};
    font-size: 1.15rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin: 0 0 0.85rem;
  }

  .package-desc {
    color: ${colors.grayText};
    line-height: 1.7;
    font-size: 0.95rem;
    margin: 0 0 1rem;
  }

  /* feature list stretches so both cards' price blocks line up at the bottom */
  ${StyledList} {
    margin: 0 0 1rem;
    flex-grow: 1;
  }

  ${StyledNote} {
    margin: 0 0 1rem;
  }

  .prices {
    margin-top: auto;
    background: ${colors.lightBg};
    border-radius: 0.4rem;
    padding: 1rem 1.25rem;

    .prices-label {
      color: ${colors.dark};
      font-weight: 700;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      margin: 0 0 0.75rem;
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;

      li {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 0.75rem;
        color: ${colors.grayText};
        font-size: 0.95rem;
        line-height: 1.5;
        padding: 0.4rem 0;
        border-bottom: 1px dashed rgba(0, 0, 0, 0.12);

        &:last-child {
          border-bottom: none;
        }
      }
    }
  }

  .discount {
    margin-top: 1rem;
    color: ${colors.dark};
    font-weight: 600;
    font-size: 0.9rem;
    line-height: 1.6;

    &:before {
      content: "🎁 ";
    }
  }

  @media (max-width: 768px) {
    padding: 1.35rem;

    .package-name {
      font-size: 1.05rem;
    }
    .package-desc,
    .prices ul li {
      font-size: 0.9rem;
    }
  }
`;

// Package presentation for the citizenship "Воевода" variant. Mirrors the
// section-1 package cards: a top accent bar and matching name colour that are
// neutral-dark for the base package and red for the featured (`.featured`)
// extended one, so the two options read the same way across both sections.
export const StyledMiniPackages = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin: 1.5rem 0;

  .mini-card {
    background: ${colors.white};
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-top: 4px solid ${colors.dark};
    border-radius: 0.5rem;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
    padding: 1.5rem;

    &.featured {
      border-top-color: ${colors.primary};

      .mini-name {
        color: ${colors.primary};
      }
    }

    .mini-name {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: ${colors.dark};
      font-size: 1.05rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      margin: 0 0 0.65rem;
    }

    .mini-text {
      color: ${colors.grayText};
      line-height: 1.7;
      font-size: 0.95rem;
      margin: 0;
    }
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    .mini-card {
      padding: 1.25rem;

      .mini-name {
        font-size: 0.95rem;
      }
      .mini-text {
        font-size: 0.9rem;
      }
    }
  }
`;

// A titled card used for the numbered "Дополнительные услуги" items and the
// grouped blocks inside section 3.
export const StyledInfoCard = styled.div`
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0.5rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.07);
  padding: 1.5rem 1.75rem;
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }

  .card-title {
    color: ${colors.dark};
    font-size: 1.05rem;
    font-weight: 700;
    margin: 0 0 0.85rem;
  }

  .card-price {
    margin-top: 1rem;
    color: ${colors.primary};
    font-weight: 700;
    font-size: 0.95rem;
    line-height: 1.6;
  }

  ${StyledStepTitle} {
    margin: 0 0 1.25rem;
  }

  ${StyledList} {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    padding: 1.25rem 1.35rem;

    .card-title {
      font-size: 0.98rem;
    }
  }
`;

// Price call-out used for single-price blocks (transcription, president route).
export const StyledPrice = styled.p`
  background: ${colors.lightBg};
  border-left: 4px solid ${colors.primary};
  border-radius: 0 0.4rem 0.4rem 0;
  padding: 1rem 1.25rem;
  margin: 1.5rem 0;
  color: ${colors.dark};
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 0.92rem;
  }
`;

// Small print at the foot of a section (transparency / state-fee reminders).
export const StyledFinePrint = styled.div`
  margin-top: 2rem;
  text-align: center;

  p {
    font-size: 0.9rem;
    font-style: italic;
    line-height: 1.7;
    margin: 0 0 0.35rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

// Inline red text cross-link (the "Иные услуги: Umiejscowienie" jump that
// scrolls down to section 3). The booking CTAs use the Button component instead.
export const StyledCta = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0.5rem 0;
  background: transparent;
  color: ${colors.primary};
  border: none;
  padding: 0.25rem 0;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.primaryDark};
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

// Centers the booking Button CTA below each pricing block.
export const StyledBookRow = styled.div`
  margin-top: 2rem;
  text-align: center;
`;
